import { getTableColumn, TableColumn } from '@shared/UI';
import { useClientDetailsTable } from './ClientDetailsTableProvider';
import { getHeaderClassName } from '@shared/UI/SimpleTable/lib/getHeaderClassName';
import { HeaderCellWithFilter } from '@shared/UI/SimpleTable/components/HeaderCellWithFilter';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
} from '@mui/material';
import { ClientDetailsBody } from '@shared/api/ServiceAPI/ClientDetails';
import { useEffect, useState } from 'react';
import useStore from '@widgets/UserManagementTable/lib/store/assign-client-store';
import useDisableStore from '@widgets/UserManagementTable/lib/store/disable-button-store';
import { deleteUser } from '@widgets/UnassignedClients/api';
import useUpdatePermissionStore from '@widgets/UserManagementTable/lib/store/update-permission-store';
import subAccountRefreshStore from '@widgets/UserManagementTable/lib/store/account-refresh-store';
import { FaUserMinus } from 'react-icons/fa';
import ResetPermissions from '@widgets/UserManagementTable/lib/store/handle-reset-permission-store';
import { ReactComponent as BinIcon } from '../../../shared/assets/svgs/BinIcon.svg';
import { ReactComponent as CheckedIcon } from '../../../shared/assets/svgs/CheckedIcon.svg';
import { ReactComponent as CrossIcon } from '../../../shared/assets/svgs/CrossIcon.svg';
import dropDownStore from '../store/drop-down-table-store';
import snackbarStore from '@widgets/MainLayout/store/snackbar-store';
import { BiCaretDown, BiCaretUp } from 'react-icons/bi';

export function getTransactionsTableColumns(
    ClientDetailsBody: any,
    fromRIA: boolean,
): TableColumn<ClientDetailsBody>[] {
    const { desc, sortFieldName } = useClientDetailsTable();
    const [userData, setUserData] = useState<any>(ClientDetailsBody);
    const [initialUserData, setInitialUserData] = useState<any>(ClientDetailsBody);
    const [updatedRows, setUpdatedRows] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
    const setClientAssigned = useStore((state) => state.setClientAssigned);
    const setDisableButton = useDisableStore((state) => state.setDisableButton);
    const setUpdatedPermissions = useUpdatePermissionStore((state) => state.setUpdatedPermission);
    const setSubAccountRefresh = subAccountRefreshStore((state) => state.setSubAccountRefresh);
    const { rowsOpen } = dropDownStore((state) => state);
    const resetPermissions = ResetPermissions();
    const { setSnackbarMessage } = snackbarStore(); // Get the method to set the message

    const handleOpenConfirmation = (rowId: number) => {
        setSelectedRowId(rowId);
        setConfirmationDialogOpen(true);
    };

    useEffect(() => {
        return () => {
            setDisableButton(false);
            setUpdatedPermissions([]);
        };
    }, [setDisableButton]);

    const handleTableNested = (rowId: number) => {
        const { setRowOpen, rowsOpen } = dropDownStore.getState();
        // Toggle the open state for the specific row
        const newState = !rowsOpen[rowId]; // Invert the current state
        setRowOpen(rowId, newState);
    };

    const handleCloseConfirmation = (event: any) => {
        setSelectedRowId(null);
        setConfirmationDialogOpen(false);
    };

    useEffect(() => {
        setUserData(ClientDetailsBody);
        setInitialUserData(ClientDetailsBody);
    }, [ClientDetailsBody]);

    useEffect(() => {
        setUserData(initialUserData);
        setUpdatedPermissions(initialUserData);
    }, [resetPermissions]);

    useEffect(() => {
        setUpdatedPermissions(updatedRows);
    }, [updatedRows]);

    const handleCheckboxChange = (row: any, value: boolean, field: string) => {
        setDisableButton(true);
        setUserData((prevData: any) =>
            prevData.map((user: any) => {
                if (user.userId === row.userId) {
                    const updatedRow = { ...user, [field]: value };

                    // Update the `updatedRows` state
                    setUpdatedRows((prevUpdatedRows) => {
                        const exists = prevUpdatedRows.some((r) => r.userId === row.userId);
                        if (exists) {
                            return prevUpdatedRows.map((r) => (r.userId === row.userId ? { ...r, [field]: value } : r));
                        }
                        return [...prevUpdatedRows, updatedRow];
                    });
                    return updatedRow;
                }
                return user;
            }),
        );
    };

    const columns: TableColumn<ClientDetailsBody>[] = [
        getTableColumn({
            dataField: 'accounts[0].id',
            flex: 0.001,
            className: 'hide-column-accountId',
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='Account ID'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            style: {
                visibility: 'collapse',
                width: 0,
                height: 0,
                padding: 0,
                margin: 0,
            } as React.CSSProperties,
            headerStyle: { display: 'none' } as React.CSSProperties,
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'accounts[0].id')}`,
        }),
        getTableColumn({
            dataField: 'accounts[0].clearingAccount',
            flex: 0.13,
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='CAN'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'accounts[0].clearingAccount')}`,
        }),
        getTableColumn({
            dataField: 'Account Name',
            flex: 0.15,
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={'Account Name'}
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'Name')}`,
            formatter: (cell, row) =>
                `${row.accounts[0].verificationRequest.firstName} ${row.accounts[0].verificationRequest.lastName}`,
        }),
        getTableColumn({
            dataField: 'canLogin',
            flex: 0.15,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='Login'
                    icon={undefined}
                    onClick={(event: any) => event.stopPropagation()}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'canLogin')}`,
            formatter: (cell, row) => (
                <Checkbox
                    color='primary'
                    checked={(() => {
                        return userData
                            ? (userData.find((user: any) => user.userId === row.userId)?.canLogin ?? false)
                            : (row.canLogin ?? false);
                    })()}
                    style={{ padding: '0px' }}
                    onClick={(event: any) => event.stopPropagation()}
                    onMouseDown={(event: any) => {
                        event.stopPropagation();
                    }}
                    onChange={(event: any) => {
                        event.stopPropagation();
                        handleCheckboxChange(row, event.target.checked, 'canLogin');
                    }}
                />
            ),
        }),
        getTableColumn({
            dataField: 'canSeePositions',
            flex: 0.15,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='See Position'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'canSeePositions')}`,
            formatter: (cell, row) => (
                <Checkbox
                    color='primary'
                    checked={(() => {
                        return userData
                            ? (userData.find((user: any) => user.userId === row.userId)?.canSeePositions ?? false)
                            : (row.canSeePositions ?? false);
                    })()}
                    style={{ padding: '0px' }}
                    onClick={(event: any) => event.stopPropagation()}
                    onMouseDown={(event: any) => {
                        event.stopPropagation();
                    }}
                    onChange={(event: any) => {
                        event.stopPropagation();
                        handleCheckboxChange(row, event.target.checked, 'canSeePositions');
                    }}
                />
            ),
        }),
        getTableColumn({
            dataField: 'canSee1099',
            flex: 0.15,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='See 1099'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'canSee1099')}`,
            formatter: (cell, row) => (
                <Checkbox
                    color='primary'
                    checked={(() => {
                        return userData
                            ? (userData.find((user: any) => user.userId === row.userId)?.canSee1099 ?? false)
                            : (row.canSee1099 ?? false);
                    })()}
                    style={{ padding: '0px' }}
                    onClick={(event: any) => event.stopPropagation()}
                    onMouseDown={(event: any) => {
                        event.stopPropagation();
                    }}
                    onChange={(event: any) => {
                        event.stopPropagation();
                        handleCheckboxChange(row, event.target.checked, 'canSee1099');
                    }}
                />
            ),
        }),
        // getTableColumn({
        //     dataField: 'setPreferences',
        //     flex: 0.15,
        //     headerFormatter: (row) => (
        //         <HeaderCellWithFilter
        //             title='Set Preferences'
        //             icon={undefined}
        //             onClick={() => null}
        //         />
        //     ),
        //     headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'setPreferences')}`,
        //     formatter: (cell, row) => (
        //         <Checkbox
        //             color='primary'
        //             checked={(() => {
        //                 return userData
        //                     ? (userData.find((user: any) => user.userId === row.userId)?.setPreferences ?? false)
        //                     : (row.setPreferences ?? false);
        //             })()}
        //             style={{ padding: '0px' }}
        //             onChange={(event) => handleCheckboxChange(row, event.target.checked, 'setPreferences')}
        //         />
        //     ),
        // }),
    ];

    if (!fromRIA) {
        columns.push(
            getTableColumn({
                dataField: 'Delete', // You can keep this as a placeholder, it won't affect the data
                flex: 0.1, // Adjust the flex value to fit your table layout
                headerFormatter: () => (
                    <HeaderCellWithFilter
                        title='Remove'
                        icon={undefined}
                        onClick={() => null}
                    />
                ),
                headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'Delete')}`,
                formatter: (_, row) => (
                    <>
                        <IconButton
                            onClick={(event: any) => {
                                handleOpenConfirmation(row.accounts[0]?.id); // Pass the row ID
                            }}
                            color='primary'
                            style={{ padding: '0px' }}
                        >
                            <FaUserMinus />
                        </IconButton>
                        {confirmationDialogOpen && selectedRowId === row.accounts[0]?.id && (
                            <Dialog
                                open={confirmationDialogOpen}
                                onClose={handleCloseConfirmation}
                                aria-labelledby='confirm-dialog-title'
                                aria-describedby='confirm-dialog-description'
                            >
                                <div
                                    style={{
                                        width: '544px',
                                        height: '364px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div
                                        style={{
                                            backgroundColor: '#FFF6F6',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            width: '100px',
                                            height: '100px',
                                            borderRadius: '50%',
                                            alignSelf: 'center',
                                            marginTop: '30px',
                                        }}
                                    >
                                        <BinIcon />
                                    </div>
                                    <DialogTitle
                                        id='confirm-dialog-title'
                                        style={{
                                            alignSelf: 'center',
                                            fontWeight: '600',
                                            fontSize: '20px',
                                            paddingBottom: '5px',
                                        }}
                                    >
                                        Confirm Delete
                                    </DialogTitle>
                                    <DialogContent>
                                        <DialogContentText
                                            id='confirm-dialog-description'
                                            style={{
                                                paddingLeft: '32px',
                                                textAlignLast: 'center',
                                                paddingBottom: '30px',
                                                fontSize: '18px',
                                                color: '#09090B66',
                                            }}
                                        >
                                            Are you sure you want to unassign this account? This action cannot be
                                            undone.
                                        </DialogContentText>
                                    </DialogContent>
                                    <DialogActions
                                        style={{ marginLeft: 'auto', marginBottom: '10px', marginRight: '5px' }}
                                    >
                                        <Button
                                            onClick={(event: any) => {
                                                handleCloseConfirmation(event);
                                            }}
                                            style={{
                                                background: 'white',
                                                color: 'black',
                                                border: '1.03px solid #E2E8F0',
                                                textTransform: 'none',
                                                fontSize: '14.36px',
                                                padding: 'revert-layer',
                                                borderRadius: '8px',
                                                width: '88px',
                                                height: '44px',
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={async (event: any) => {
                                                setIsLoading(true);
                                                try {
                                                    await deleteUser(selectedRowId); // Use the selectedRowId
                                                    setSnackbarMessage('Successfully Removed the Client Account');
                                                } catch {
                                                    setSnackbarMessage('Failed to Remove the Client Account');
                                                }
                                                handleCloseConfirmation(event);
                                                setClientAssigned(true);
                                                setSubAccountRefresh(true);
                                                setIsLoading(false);
                                            }}
                                            color='secondary'
                                            autoFocus
                                            disabled={isLoading}
                                            style={{
                                                background: '#0066A6',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontSize: '14.36px',
                                                padding: 'revert-layer',
                                                borderRadius: '8px',
                                                width: '88px',
                                                height: '44px',
                                            }}
                                        >
                                            {isLoading ? <CircularProgress size={24} /> : 'Confirm'}
                                        </Button>
                                    </DialogActions>
                                </div>
                            </Dialog>
                        )}
                    </>
                ),
            }),
        );
    }

    if (fromRIA) {
        columns.push(
            getTableColumn({
                dataField: 'isRebalanced',
                flex: 0.1,
                headerFormatter: (row) => (
                    <HeaderCellWithFilter
                        title={'Rebalanced'}
                        icon={undefined}
                        onClick={() => null}
                    />
                ),
                formatter: (cell, row) => (row.isRebalanced ? <CheckedIcon /> : <CrossIcon />),
            }),
            getTableColumn({
                dataField: 'Delete', // You can keep this as a placeholder, it won't affect the data
                flex: 0.2, // Adjust the flex value to fit your table layout
                headerFormatter: () => (
                    <HeaderCellWithFilter
                        title='Assigned Securities'
                        icon={undefined}
                        onClick={() => null}
                    />
                ),
                headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'Delete')}`,
                formatter: (_, row) => {
                    const isRowOpen = rowsOpen[row.accounts[0]?.id];
                    return (
                        <IconButton
                            onClick={(event: any) => {
                                event.stopPropagation();
                                handleTableNested(row.accounts[0]?.id); // Pass the row ID
                            }}
                            color='primary'
                            style={{ padding: '0px' }}
                        >
                            {isRowOpen ? <BiCaretUp /> : <BiCaretDown />} {/* Toggle the icon */}
                        </IconButton>
                    );
                },
            }),
        );
    }
    return columns;
}
